/**
 * TT-02 — deriving the pilot's anchor chips from `GET /my-context`.
 *
 * The pilot never sees `entityType` / `entityId`. He sees three places, taken
 * from his own assignment walked upward: inspection task → contract position
 * (turbine) → contract (park) → project. This module is the whole of that
 * derivation, kept pure so it is unit-tested rather than clicked through.
 *
 * Labels are park and turbine NAMES. A contract number never appears — the
 * contract is what is stored underneath (invoicing follows it); the pilot's
 * vocabulary is places.
 */
import type {
  AnchorLevel,
  MyTimeTrackingContextItem,
  StandbyType,
  TimeTrackingEntityType,
} from "./types.js";
import { ENTITY_TYPE_BY_ANCHOR_LEVEL } from "./types.js";

export interface AnchorChoice {
  level: AnchorLevel;
  entityType: TimeTrackingEntityType;
  entityId: string;
  /** What the chip says, e.g. `"WT 04 (this turbine)"`, `"Whole park · Gevancas II"`. */
  label: string;
}

/**
 * The assignment the form opens on: the one the BE flagged `isActive` (the
 * instruction in progress, else the most recently planned open one). Falls back
 * to the head of the list, which the BE already sorted the same way.
 */
export function activeAssignment(
  assignments: readonly MyTimeTrackingContextItem[] | undefined,
): MyTimeTrackingContextItem | null {
  if (!assignments || assignments.length === 0) return null;
  return assignments.find((a) => a.isActive) ?? assignments[0] ?? null;
}

/**
 * The three chips for one assignment, in mockup order (turbine, park, project).
 *
 * A level whose id is missing is OMITTED rather than rendered disabled: a
 * field-created instruction with no contract position has no turbine to book
 * against, and a dead chip on a phone is a target that wastes a tap.
 */
export function anchorChoices(
  assignment: MyTimeTrackingContextItem | null,
): AnchorChoice[] {
  if (!assignment) return [];
  const out: AnchorChoice[] = [];
  if (assignment.contractPositionId) {
    out.push({
      level: "turbine",
      entityType: ENTITY_TYPE_BY_ANCHOR_LEVEL.turbine,
      entityId: assignment.contractPositionId,
      label: assignment.turbineLabel
        ? `${assignment.turbineLabel} (this turbine)`
        : "This turbine",
    });
  }
  if (assignment.contractId) {
    out.push({
      level: "park",
      entityType: ENTITY_TYPE_BY_ANCHOR_LEVEL.park,
      entityId: assignment.contractId,
      label: assignment.parkLabel
        ? `Whole park · ${assignment.parkLabel}`
        : "Whole park",
    });
  }
  if (assignment.projectId) {
    out.push({
      level: "project",
      entityType: ENTITY_TYPE_BY_ANCHOR_LEVEL.project,
      entityId: assignment.projectId,
      label: assignment.projectLabel
        ? `Project · ${assignment.projectLabel}`
        : "Project",
    });
  }
  return out;
}

/**
 * Which chip is pre-selected for a given standby type.
 *
 * **Weather pre-selects the PARK** — that is the case legacy turbine-only
 * anchoring got wrong: a five-hour rain hold covers the whole site, and booking
 * it onto one turbine made the invoice unreadable. **Technical pre-selects the
 * TURBINE**, because a technical hold is about one machine.
 *
 * Falls back down the list when the preferred level is unavailable, so a pilot
 * on a field-created instruction still gets a sensible default rather than none.
 */
export function defaultAnchorFor(
  standbyType: StandbyType,
  choices: readonly AnchorChoice[],
): AnchorChoice | null {
  if (choices.length === 0) return null;
  const order: AnchorLevel[] =
    standbyType === "standby_weather"
      ? ["park", "project", "turbine"]
      : ["turbine", "park", "project"];
  for (const level of order) {
    const hit = choices.find((c) => c.level === level);
    if (hit) return hit;
  }
  return choices[0] ?? null;
}

/**
 * Label for the assignment picker (the no-active-task branch). Park first, then
 * turbine — a pilot scanning a list looks for the site he drove to.
 */
export function assignmentLabel(item: MyTimeTrackingContextItem): string {
  const parts = [item.parkLabel, item.turbineLabel].filter((p): p is string =>
    Boolean(p),
  );
  if (parts.length > 0) return parts.join(" · ");
  return `Instruction ${String(item.instructionCode)}`;
}
